import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import {
  Box, Card, Paper, Typography, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  AreaChart, Area,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import axios from '../api';
import merchantService from '../services/merchant.service';
import { useAuth } from '../contexts/AuthContext';
import LoadingAnimation from '../components/LoadingAnimation';

export default function Analytics() {
  const { user } = useAuth();
  const isMerchant = user?.role === 'merchant';
  const [period, setPeriod] = useState('Weekly');
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setError('');
      let response;
      if (isMerchant) {
        console.log('Fetching merchant analytics...');
        response = await merchantService.getAnalytics({ period });
        console.log('Merchant analytics response:', response);
      } else {
        response = await axios.get('/api/operations/analytics', { params: { period } });
        response = response.data;
      }
      setStats(response);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching analytics:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to load analytics';
      setError(errorMsg);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingAnimation
        message="Loading analytics..."
        subtitle={isMerchant ? "Analyzing your business performance" : "Gathering platform insights"}
      />
    );
  }

  if (error || !stats) return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h6" color="error" sx={{ mb: 2 }}>
        Failed to load analytics
      </Typography>
      {error && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        Please check the browser console for more details
      </Typography>
    </Box>
  );

  const pieData = isMerchant
    ? stats.topProducts || []
    : [
        { name: 'Active', value: stats.totalUsers || 0, fill: '#0B6E4F' },
        { name: 'Pending', value: stats.pendingBusinesses || 0, fill: '#FF6B35' },
      ];

  // Normalize the orders/revenue trend for the chart. The admin endpoint groups
  // by day and returns { _id: 'YYYY-MM-DD', orders, revenue }; the merchant
  // endpoint already returns { name, orders }. Map both to { name, orders,
  // revenue } and give the X axis a short readable label.
  const trendData = (stats.ordersTrend || []).map((point: any) => {
    let name = point.name;
    if (!name && point._id) {
      const d = new Date(point._id);
      name = isNaN(d.getTime())
        ? point._id
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return {
      name: name || '',
      orders: point.orders || 0,
      revenue: Number(point.revenue || 0),
    };
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isMerchant ? 'My Analytics' : 'Analytics'}
          </Typography>
          <Typography color="text.secondary">
            {isMerchant
              ? 'Track your business performance and customer engagement'
              : 'Platform insights and performance metrics.'}
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, value) => value && setPeriod(value)}
          size="small"
        >
          <ToggleButton value="Daily">Daily</ToggleButton>
          <ToggleButton value="Weekly">Weekly</ToggleButton>
          <ToggleButton value="Monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Key Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Box>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              {isMerchant ? 'Customer Reviews' : 'Total Users'}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {isMerchant ? stats.reviewCount : stats.totalUsers}
            </Typography>
          </Paper>
        </Box>
        <Box>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              {isMerchant ? 'Active Products' : 'Active Users'}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
              {isMerchant ? stats.activeBoxes : stats.activeUsers}
            </Typography>
          </Paper>
        </Box>
        <Box>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Total Orders</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
              {stats.totalOrders}
            </Typography>
          </Paper>
        </Box>
        <Box>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Total Revenue</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
              <RialSymbol /> {stats.revenue?.total?.toFixed(2) || 0}
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        <Box>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Orders & Revenue Trend</Typography>
            {trendData.length === 0 ? (
              <Box sx={{ py: 10, textAlign: 'center' }}>
                <Typography color="text.secondary">No order activity for this period yet</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0B6E4F" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0B6E4F" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    formatter={(value: any, name: any) =>
                      name === 'Revenue' ? [`OMR ${Number(value).toFixed(2)}`, name] : [value, name]
                    }
                  />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="orders"
                    name="Orders"
                    stroke="#0B6E4F"
                    fill="url(#colorOrders)"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue"
                    stroke="#FF6B35"
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Box>

        <Box>
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {isMerchant ? 'Top Products' : 'Platform Distribution'}
            </Typography>
            {isMerchant && pieData.length === 0 ? (
              <Box sx={{ py: 8 }}>
                <Typography color="text.secondary">No products data yet</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={(entry) => entry.name}
                  >
                    {pieData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.fill || `hsl(${index * 60}, 70%, 50%)`} />
                    ))}
                  </Pie>
                  <Tooltip />
                  {!isMerchant && <Legend />}
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Box>
      </Box>

      {/* Revenue Breakdown */}
      <Card sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Revenue Breakdown</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <Box>
            <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'white' }}>
              <Typography variant="caption">Today</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                <RialSymbol /> {stats.revenue?.today?.toFixed(2)}
              </Typography>
            </Paper>
          </Box>
          <Box>
            <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
              <Typography variant="caption">This Month</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                <RialSymbol /> {stats.revenue?.month?.toFixed(2)}
              </Typography>
            </Paper>
          </Box>
          <Box>
            <Paper sx={{ p: 2, bgcolor: 'warning.light', color: 'white' }}>
              <Typography variant="caption">All Time</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                <RialSymbol /> {stats.revenue?.total?.toFixed(2)}
              </Typography>
            </Paper>
          </Box>
          <Box>
            <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'white' }}>
              <Typography variant="caption">Rescued Boxes</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                {stats.rescuedBoxes} boxes
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
